import * as RSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { useId, type ReactElement, type ReactNode } from "react";

import { cn } from "../utils/cn";
import { Label } from "./Label";

export type SelectOption = string | { value: string; label: string; disabled?: boolean };

export interface SelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
  options: readonly SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  size?: "sm" | "md" | "lg";
  /** Forwarded to the Radix Trigger so screen readers and tests can find
   * an unlabeled Select (e.g. a filter bar where the visual label is
   * elsewhere). */
  "aria-label"?: string;
}

const TRIGGER_BASE =
  "inline-flex w-full items-center justify-between gap-2 rounded-md border bg-neutral-0 text-sm text-text-primary outline-none transition-shadow " +
  "data-[placeholder]:text-text-tertiary " +
  "focus-visible:ring-2 focus-visible:ring-primary-500/40 " +
  "disabled:cursor-default disabled:bg-neutral-50 disabled:text-text-disabled";

const TRIGGER_SIZES = {
  sm: "h-8 px-3",
  md: "h-10 px-4",
  lg: "h-12 px-4",
};

const CONTENT =
  "z-50 min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border border-border-default bg-neutral-0 shadow-lg";

const ITEM =
  "relative flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-sm text-text-primary outline-none " +
  "data-[highlighted]:bg-primary-50 data-[highlighted]:text-primary-700 " +
  "data-[state=checked]:font-semibold " +
  "data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled";

function normalize(opt: SelectOption): { value: string; label: string; disabled?: boolean } {
  return typeof opt === "string" ? { value: opt, label: opt } : opt;
}

export function Select({
  label,
  hint,
  error,
  helper,
  required,
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Bitte wählen…",
  disabled,
  name,
  id,
  size = "md",
  "aria-label": ariaLabel,
}: SelectProps): ReactElement {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const errorId = `${triggerId}-error`;
  const helperId = `${triggerId}-helper`;

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={triggerId} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <RSelect.Root
        {...(value !== undefined && { value })}
        {...(defaultValue !== undefined && { defaultValue })}
        {...(onValueChange !== undefined && { onValueChange })}
        {...(disabled !== undefined && { disabled })}
        {...(name !== undefined && { name })}
        {...(required !== undefined && { required })}
      >
        <RSelect.Trigger
          id={triggerId}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errorId : helper ? helperId : undefined}
          {...(ariaLabel !== undefined && { "aria-label": ariaLabel })}
          className={cn(
            TRIGGER_BASE,
            TRIGGER_SIZES[size],
            error ? "border-danger-500 focus-visible:ring-danger-500/30" : "border-neutral-300",
          )}
        >
          <RSelect.Value placeholder={placeholder} />
          <RSelect.Icon className="text-neutral-500">
            <ChevronDown size={16} />
          </RSelect.Icon>
        </RSelect.Trigger>
        <RSelect.Portal>
          <RSelect.Content className={CONTENT} position="popper" sideOffset={4}>
            <RSelect.Viewport className="p-1">
              {options.map((opt) => {
                const o = normalize(opt);
                return (
                  <RSelect.Item
                    key={o.value}
                    value={o.value}
                    {...(o.disabled !== undefined && { disabled: o.disabled })}
                    className={ITEM}
                  >
                    <RSelect.ItemIndicator className="absolute right-2 inline-flex items-center text-primary-600">
                      <Check size={14} />
                    </RSelect.ItemIndicator>
                    <RSelect.ItemText>{o.label}</RSelect.ItemText>
                  </RSelect.Item>
                );
              })}
            </RSelect.Viewport>
          </RSelect.Content>
        </RSelect.Portal>
      </RSelect.Root>
      {error ? (
        <p id={errorId} className="mt-2 text-xs text-danger-600">
          {error}
        </p>
      ) : helper ? (
        <p id={helperId} className="mt-2 text-xs text-text-tertiary">
          {helper}
        </p>
      ) : null}
    </div>
  );
}
